import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigEntity } from '@repository/config/entities/config.entity';
import { EnvironmentApiService } from '@api/config/environment-api.service';
import { WINDOW } from '@providers/window.provider';
import { ApiConfiguration, Configuration } from '../../types/configuration/Configuration';
import { ApiUrlEntity } from './entities/api-url.entity';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentRepositoryService {
  public apiUrl!: ApiUrlEntity;

  public constructor(
    private envApi: EnvironmentApiService,
    @Inject(WINDOW) private window: Window,
  ) {}

  public getConfig(): Observable<Configuration> {
    return this.envApi.getConfig();
  }

  public buildApiUrlEntity(config: Configuration): ApiUrlEntity {
    const baseUrl = this.getBaseUrl(config.api, this.createRestApiUrlFallbacks());
    const emsBaseWsUrl = this.getBaseUrl(config.ems, this.createEmsUrlFallbacks());
    return {
      baseUrl,
      authUrl: `${baseUrl}/v1/auth/`,
      apiUrl: `${baseUrl}/v1/api/`,
      apiUrlV2: `${baseUrl}/v2/api/`,
      apiUtilUrl: `${baseUrl}/v1/util/`,
      emsWsURL: `${emsBaseWsUrl}/ems/ws/ui/`,
      cardConfigUrl: config.carDatabaseUrl,
      captchaKey: config.captchaApiKey,
      mixpanelApiKey: config.mixpanelApiKey,
      assetsUrl: `https://${config.assetsUrl}`,
    };
  }

  public getConfigEntity(): Observable<ConfigEntity> {
    return this.getConfig().pipe(
      map((config) => ({
        apiUrlEntity: this.buildApiUrlEntity(config),
        auth0Config: config.auth0,
      })),
    );
  }

  private createEmsUrlFallbacks(): ApiConfiguration {
    try {
      const protocol = this.window.location.protocol === 'https:' ? 'wss' : 'ws';
      const hostname = this.window.location.hostname || '';
      let host: string;
      if (hostname === 'localhost') {
        host = 'localhost';
      } else {
        const parts = hostname.split('.');
        host = `api.${parts.slice(-2).join('.')}`;
      }
      return { protocol, host };
    } catch (ex) {
      console.error(ex);
      return { protocol: 'wss', host: 'api.vev-iq.com' };
    }
  }

  private createRestApiUrlFallbacks(): ApiConfiguration {
    try {
      const protocol = this.window?.location.protocol.slice(0, -1);
      const host = this.window?.location.hostname;
      return { protocol, host };
    } catch (ex) {
      console.error(ex);
      return { protocol: 'https', host: 'api.vev-iq.com' };
    }
  }

  private getBaseUrl(apiConfiguration: ApiConfiguration, fallbacks: ApiConfiguration): string {
    const protocol = apiConfiguration?.protocol || fallbacks.protocol;
    const host = apiConfiguration?.host || fallbacks.host;
    const port = apiConfiguration?.port || fallbacks.port;

    const baseUrl = `${protocol}://${host}`;
    if (port) {
      return `${baseUrl}:${port}`;
    }

    return baseUrl;
  }
}
